// client/src/pages/secure/TerritoriesPage.js — territories as sets of ZIP codes,
// municipalities and counties, with primary/backup agents and property classes.
// Everyone sees the list (and which ones are theirs); the managing broker and office
// manager create and edit. No map: matching is by ZIP / town / county (a map provider
// is an owner decision and is off).
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';
import { Card, Empty, ErrorLine, Btn, DocLinks, CLASSES, input } from '../../components/secure/territory/Shared';

const blank = { name: '', description: '', property_types: [], is_farm_area: false, stale_after_days: '', is_active: true, areas: [], agents: [] };

const parseAreas = (text, type, state) => text.split(/[\n,;]+/).map((v) => v.trim()).filter(Boolean)
    .map((v) => ({ area_type: type, area_value: v, state: state || null }));

const Editor = ({ initial, agents, places, onSaved, onCancel }) => {
    const api = useApiClient();
    const [t, setT] = useState(() => ({ ...blank, ...initial,
        property_types: initial?.property_types ? String(initial.property_types).split(',') : [],
        is_farm_area: !!initial?.is_farm_area, is_active: initial ? !!initial.is_active : true,
        stale_after_days: initial?.stale_after_days || '' }));
    const [bulk, setBulk] = useState({ type: 'zip', text: '', state: '' });
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const addBulk = () => {
        const add = parseAreas(bulk.text, bulk.type, bulk.state);
        setT({ ...t, areas: [...t.areas, ...add] });
        setBulk({ ...bulk, text: '' });
    };
    const toggleClass = (c) => setT({ ...t, property_types: t.property_types.includes(c) ? t.property_types.filter((x) => x !== c) : [...t.property_types, c] });
    const setAgentRole = (agentId, role) => {
        const rest = t.agents.filter((a) => Number(a.agent_id) !== Number(agentId));
        setT({ ...t, agents: role ? [...rest, { agent_id: agentId, role }] : rest });
    };
    const save = async () => {
        setBusy(true); setError('');
        try {
            const body = { ...t, property_types: t.property_types.join(','), stale_after_days: t.stale_after_days || null };
            const saved = initial?.territory_id
                ? await api.put(`/api/territory/territories/${initial.territory_id}`, body)
                : await api.post('/api/territory/territories', body);
            onSaved(saved);
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    return (
        <Card title={initial?.territory_id ? `Edit ${initial.name}` : 'New territory'}>
            <ErrorLine error={error} />
            <div className="grid md:grid-cols-2 gap-3">
                <label className="text-sm">Name<input className={input} value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} /></label>
                <label className="text-sm">Flag as not worked after (days, blank = office default)
                    <input className={input} type="number" min="1" value={t.stale_after_days} onChange={(e) => setT({ ...t, stale_after_days: e.target.value })} /></label>
                <label className="text-sm md:col-span-2">Description<input className={input} value={t.description || ''} onChange={(e) => setT({ ...t, description: e.target.value })} /></label>
            </div>
            <div className="flex flex-wrap gap-4 my-3 text-sm">
                <span className="text-slate-600">Property classes (none = all):</span>
                {CLASSES.map((c) => (
                    <label key={c} className="flex items-center gap-1"><input type="checkbox" checked={t.property_types.includes(c)} onChange={() => toggleClass(c)} />{c}</label>
                ))}
                <label className="flex items-center gap-1"><input type="checkbox" checked={t.is_farm_area} onChange={(e) => setT({ ...t, is_farm_area: e.target.checked })} />Farm area</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={t.is_active} onChange={(e) => setT({ ...t, is_active: e.target.checked })} />Active</label>
            </div>

            <h3 className="font-medium text-slate-700 mt-4 mb-1">Areas</h3>
            <div className="flex flex-wrap gap-2 mb-2">
                {t.areas.length === 0 && <Empty>No areas yet — add ZIP codes, municipalities or counties.</Empty>}
                {t.areas.map((a, i) => (
                    <span key={`${a.area_type}-${a.area_value}-${i}`} className="text-xs bg-slate-100 rounded-full px-2 py-1">
                        {a.area_type === 'zip' ? 'ZIP' : a.area_type}: {a.area_value}{a.state ? ` (${a.state})` : ''}
                        <button type="button" className="ml-1 text-red-600" onClick={() => setT({ ...t, areas: t.areas.filter((_, j) => j !== i) })}>×</button>
                    </span>
                ))}
            </div>
            <div className="flex flex-wrap gap-2 items-end">
                <select className="p-2 border rounded text-sm" value={bulk.type} onChange={(e) => setBulk({ ...bulk, type: e.target.value })}>
                    <option value="zip">ZIP codes</option><option value="municipality">Municipalities</option><option value="county">Counties</option>
                </select>
                <input className="p-2 border rounded text-sm flex-1 min-w-[200px]" list="place-suggestions" value={bulk.text}
                    placeholder={bulk.type === 'zip' ? '60657, 60613 …' : 'one or more, comma-separated'} onChange={(e) => setBulk({ ...bulk, text: e.target.value })} />
                {bulk.type !== 'zip' && <input className="p-2 border rounded text-sm w-20" placeholder="State" maxLength={2} value={bulk.state} onChange={(e) => setBulk({ ...bulk, state: e.target.value.toUpperCase() })} />}
                <Btn kind="secondary" onClick={addBulk} disabled={!bulk.text.trim()}>Add</Btn>
                <datalist id="place-suggestions">
                    {places.map((p, i) => <option key={i} value={bulk.type === 'county' ? (p.county || '') : p.municipality} />)}
                </datalist>
            </div>

            <h3 className="font-medium text-slate-700 mt-4 mb-1">Agents</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1 text-sm max-h-64 overflow-y-auto">
                {agents.map((a) => {
                    const cur = t.agents.find((x) => Number(x.agent_id) === Number(a.agent_id));
                    return (
                        <label key={a.agent_id} className="flex items-center justify-between gap-2 border rounded px-2 py-1">
                            <span>{a.name}</span>
                            <select className="p-1 border rounded text-xs" value={cur?.role || ''} onChange={(e) => setAgentRole(a.agent_id, e.target.value)}>
                                <option value="">—</option><option value="primary">primary</option><option value="backup">backup</option>
                            </select>
                        </label>
                    );
                })}
            </div>
            <div className="flex gap-2 mt-4">
                <Btn onClick={save} disabled={busy || !t.name.trim()}>{busy ? 'Saving…' : 'Save territory'}</Btn>
                <Btn kind="secondary" onClick={onCancel}>Cancel</Btn>
            </div>
        </Card>
    );
};

const TerritoriesPage = () => {
    const api = useApiClient();
    const me = getCurrentUser();
    const mgr = canManageStaff(me);
    const [list, setList] = useState([]);
    const [agents, setAgents] = useState([]);
    const [places, setPlaces] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(null);
    const [error, setError] = useState('');
    const [lookup, setLookup] = useState({ zip_code: '', city: '', state: '' });
    const [match, setMatch] = useState(null);

    const load = useCallback(async () => {
        try { setList(await api.get(`/api/territory/territories${mgr ? '?all=1' : ''}`)); setError(''); }
        catch (e) { setError(e.message); }
    }, [api, mgr]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!mgr) return;
        api.get('/api/routing/agents').then(setAgents).catch(() => setAgents([]));
        api.get('/api/territory/place-suggestions').then(setPlaces).catch(() => setPlaces([]));
    }, [api, mgr]);

    const remove = async (t) => {
        if (!window.confirm(`Remove ${t.name}? Its visit history is kept.`)) return;
        try { await api.delete(`/api/territory/territories/${t.territory_id}`); load(); } catch (e) { setError(e.message); }
    };
    const findMatch = async () => {
        const q = new URLSearchParams(Object.entries(lookup).filter(([, v]) => v)).toString();
        try { setMatch(await api.get(`/api/territory/match?${q}`)); } catch (e) { setError(e.message); }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-800">Territories</h1>
                {mgr && !editing && <Btn onClick={() => setEditing({})}>New territory</Btn>}
            </div>
            <ErrorLine error={error} />
            {editing && <Editor initial={editing.territory_id ? editing : null} agents={agents} places={places}
                onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

            <Card title="Which territory is an address in?">
                <div className="flex flex-wrap gap-2">
                    <input className="p-2 border rounded text-sm w-28" placeholder="ZIP" value={lookup.zip_code} onChange={(e) => setLookup({ ...lookup, zip_code: e.target.value })} />
                    <input className="p-2 border rounded text-sm w-48" placeholder="Municipality" value={lookup.city} onChange={(e) => setLookup({ ...lookup, city: e.target.value })} />
                    <input className="p-2 border rounded text-sm w-20" placeholder="State" value={lookup.state} onChange={(e) => setLookup({ ...lookup, state: e.target.value })} />
                    <Btn kind="secondary" onClick={findMatch}>Look up</Btn>
                </div>
                {match && <p className="text-sm mt-2">{match.length ? match.map((m) => `${m.name} (by ${m.matched_on} ${m.value})`).join('; ') : 'No territory covers that address.'}</p>}
            </Card>

            {list.length === 0 ? <Card><Empty>No territories yet.{mgr ? ' Create one to start routing leads and tracking coverage.' : ''}</Empty></Card> : (
                <div className="grid lg:grid-cols-2 gap-4">
                    {list.map((t) => (
                        <Card key={t.territory_id} title={<span>{t.name}{!t.is_active && <span className="ml-2 text-xs text-slate-500">(inactive)</span>}
                            {t.my_role && <span className="ml-2 text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">yours — {t.my_role}</span>}
                            {!!t.is_farm_area && <span className="ml-2 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">farm area</span>}</span>}
                            right={mgr && <div className="flex gap-2"><Btn kind="secondary" onClick={() => setEditing(t)}>Edit</Btn><Btn kind="danger" onClick={() => remove(t)}>Remove</Btn></div>}>
                            {t.description && <p className="text-sm text-slate-600 mb-2">{t.description}</p>}
                            <p className="text-sm"><span className="text-slate-500">Classes:</span> {t.property_types || 'all'}</p>
                            <p className="text-sm"><span className="text-slate-500">Areas:</span> {t.areas.length ? t.areas.map((a) => (a.area_type === 'zip' ? a.area_value : `${a.area_value}${a.area_type === 'county' ? ' County' : ''}${a.state ? `, ${a.state}` : ''}`)).join(' · ') : '—'}</p>
                            <p className="text-sm"><span className="text-slate-500">Agents:</span> {t.agents.length ? t.agents.map((a) => `${a.name}${a.role === 'backup' ? ' (backup)' : ''}`).join(', ') : 'none — leads here go to the fallback'}</p>
                            <button type="button" className="text-xs text-indigo-700 mt-2" onClick={() => setOpen(open === t.territory_id ? null : t.territory_id)}>
                                {open === t.territory_id ? 'Hide documents' : 'Documents (farm lists, mailers, maps)'}
                            </button>
                            {open === t.territory_id && <div className="mt-2"><DocLinks entityType="territory" entityId={t.territory_id} canEdit={mgr} /></div>}
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TerritoriesPage;
